/**
 * Field-photo pipeline: geolocalización + marca de agua forense.
 *
 * `capturePhotoWithGeo(file, meta)` es el helper que reemplaza al viejo
 * `compressPhoto`. Combina 3 pasos en uno:
 *   1. Pide `navigator.geolocation.getCurrentPosition` (soft: no bloquea).
 *   2. Comprime la imagen a JPEG ≤ 1000px lado mayor.
 *   3. Estampa una franja negra semitransparente al pie con fecha/hora
 *      UTC, coordenadas lat/lng ±accuracy y el nombre del reportero.
 *
 * Devuelve `{ dataUrl, geo }` donde `geo` es `null` si el usuario negó
 * el permiso o el navegador no lo soporta.
 */

const MAX_SIDE = 1000;
const JPEG_QUALITY = 0.72;
const GEO_TIMEOUT_MS = 6000;

/** Wrap navigator.geolocation.getCurrentPosition in a Promise. */
function requestGeolocation() {
  if (!("geolocation" in navigator)) return Promise.resolve(null);
  return new Promise((resolve) => {
    let done = false;
    const finish = (val) => { if (!done) { done = true; resolve(val); } };
    navigator.geolocation.getCurrentPosition(
      (pos) => finish({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy_m: pos.coords.accuracy,
        captured_at: new Date(pos.timestamp).toISOString(),
        source: "browser_geolocation",
      }),
      () => finish(null),
      { enableHighAccuracy: true, timeout: GEO_TIMEOUT_MS, maximumAge: 30_000 },
    );
    setTimeout(() => finish(null), GEO_TIMEOUT_MS + 500);
  });
}

/** Load a File as HTMLImageElement, respecting orientation. */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

/** Fit an image into MAX_SIDE while preserving aspect ratio. */
function fitDimensions(w, h) {
  if (w <= MAX_SIDE && h <= MAX_SIDE) return { width: w, height: h };
  if (w >= h) return { width: MAX_SIDE, height: Math.round(h * (MAX_SIDE / w)) };
  return { width: Math.round(w * (MAX_SIDE / h)), height: MAX_SIDE };
}

/** Format a Date in local timezone for the stamp. */
function formatStampTs(iso) {
  try {
    const d = iso ? new Date(iso) : new Date();
    return d.toLocaleString("es-PE", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    });
  } catch { return new Date().toISOString().slice(0, 19).replace("T", " "); }
}

/** Draw a 3-line watermark strip at the bottom of the canvas. */
function drawWatermark(ctx, width, height, lines) {
  const padding = 10;
  const lineHeight = Math.max(14, Math.round(height * 0.028));
  const stripHeight = lineHeight * lines.length + padding * 2;
  ctx.save();
  ctx.fillStyle = "rgba(0, 0, 0, 0.62)";
  ctx.fillRect(0, height - stripHeight, width, stripHeight);
  ctx.fillStyle = "#ffffff";
  ctx.font = `600 ${Math.round(lineHeight * 0.68)}px system-ui, sans-serif`;
  ctx.textBaseline = "top";
  ctx.shadowColor = "rgba(0,0,0,0.6)";
  ctx.shadowBlur = 3;
  lines.forEach((txt, i) => {
    ctx.fillText(txt, padding, height - stripHeight + padding + i * lineHeight);
  });
  ctx.restore();
}

/**
 * Main entry point.
 * @param {File} file — image blob from <input type="file">.
 * @param {object} meta — `{ reporter, area }` to render on the watermark.
 * @returns {Promise<{ dataUrl: string, geo: object | null }>}
 */
export async function capturePhotoWithGeo(file, meta = {}) {
  // Kick geolocation off in parallel with image decode so total latency is min(both).
  const [geo, img] = await Promise.all([requestGeolocation(), loadImage(file)]);
  const { width, height } = fitDimensions(img.width, img.height);
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);

  const ts = formatStampTs(geo?.captured_at);
  const lines = [`${ts}  ·  ${meta.reporter || "sin reportero"}`];
  if (geo) {
    const acc = geo.accuracy_m ? ` ±${Math.round(geo.accuracy_m)}m` : "";
    lines.push(`GPS ${geo.lat.toFixed(6)}, ${geo.lng.toFixed(6)}${acc}`);
  } else {
    lines.push("GPS no disponible");
  }
  if (meta.area) lines.push(`Area: ${meta.area}`);

  drawWatermark(ctx, width, height, lines);
  const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
  return { dataUrl, geo };
}

/** Backwards-compatible thin wrapper for callers that don't need geo yet. */
export async function compressPhoto(file) {
  const { dataUrl } = await capturePhotoWithGeo(file, {});
  return dataUrl;
}
